/**
 * VP-175 — tryb „Z adresu strony (AI)" wizarda.
 *
 * Jeden input: adres strony usera. AI analizuje stronę (express-init: scrape +
 * arkusz ofertowy + propozycje zdjęć), potem pisze teksty reklamy
 * (generate-ad-copy z arkusza). Zero pytań o cele, KPI czy strukturę kampanii —
 * wynik trafia jako prefill do panelu „przejrzyj i uruchom".
 */
import { useState } from "react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { ArrowLeft, Loader2, Sparkles } from "lucide-react";
import type { AdReviewPrefill } from "./ad-review-panel";

type ExpressInitResponse = {
  offerSheetId: string | null;
  offerSheetContent: string;
  suggestedGoals: { monthlyBudget: number };
  productImageUrls: string[];
  scraped: { name: string; description: string };
};

type AdCopyResponse = { primaryTexts?: string[]; headlines?: string[] };

const DAYS_PER_MONTH = 30.44;
const MIN_DAILY_BUDGET = 20;

type Props = {
  onReady: (prefill: AdReviewPrefill) => void;
  onBack: () => void;
};

export default function UrlAdFlow({ onReady, onBack }: Props): JSX.Element {
  const { toast } = useToast();
  const [url, setUrl] = useState("");
  const [analyzing, setAnalyzing] = useState(false);

  const handleAnalyze = async (): Promise<void> => {
    const trimmed = url.trim();
    const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    setAnalyzing(true);
    try {
      const init = (await apiRequest("POST", "/api/campaign-creator/express-init", {
        url: normalized,
      })) as ExpressInitResponse;

      // Teksty reklamy z arkusza ofertowego. Jak AI nie da rady — user dopisze
      // je sam w panelu (pola są edytowalne), nie blokujemy flow.
      let headline = "";
      let primaryText = "";
      try {
        const copy = (await apiRequest("POST", "/api/campaign-creator/generate-ad-copy", {
          mode: "from_offer_sheet",
          offerSheetId: init.offerSheetId ?? undefined,
          manualLpText: init.offerSheetId ? undefined : init.offerSheetContent,
          count: 1,
        })) as AdCopyResponse;
        headline = copy.headlines?.[0] ?? "";
        primaryText = copy.primaryTexts?.[0] ?? "";
      } catch {
        toast({
          title: "Nie udało się napisać tekstów automatycznie",
          description: "Uzupełnij nagłówek i tekst reklamy ręcznie w następnym kroku.",
        });
      }

      const monthly = init.suggestedGoals?.monthlyBudget;
      const dailyBudget =
        monthly && monthly > 0
          ? Math.max(MIN_DAILY_BUDGET, Math.round(monthly / DAYS_PER_MONTH))
          : 50;

      onReady({
        campaignName: init.scraped.name?.slice(0, 80),
        linkUrl: normalized,
        headline,
        primaryText,
        dailyBudget,
        suggestedImages: init.productImageUrls ?? [],
      });
    } catch (err) {
      toast({
        title: "Nie udało się przeanalizować strony",
        description: err instanceof Error ? err.message : "Sprawdź adres i spróbuj ponownie.",
        variant: "destructive",
      });
    } finally {
      setAnalyzing(false);
    }
  };

  if (analyzing) {
    return (
      <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-6 text-center space-y-3">
        <Loader2 className="w-8 h-8 animate-spin text-[#ef3e29] mx-auto" />
        <div className="text-sm font-medium text-white">AI przygotowuje Twoją reklamę…</div>
        <p className="text-xs text-white/50 max-w-sm mx-auto">
          Analizujemy Twoją stronę, piszemy teksty i wybieramy zdjęcia. To potrwa około minuty.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4">
        <Label className="text-xs text-white/60">Adres Twojej strony internetowej</Label>
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && url.trim()) void handleAnalyze();
          }}
          placeholder="https://twojastrona.pl"
          className="mt-1"
        />
        <p className="text-[11px] text-white/40 mt-2">
          AI przeczyta stronę i sama przygotuje teksty oraz zdjęcia do reklamy. Wszystko będziesz
          mógł jeszcze poprawić przed uruchomieniem.
        </p>
      </div>
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="text-xs text-white/40 hover:text-white/70 flex items-center gap-1"
        >
          <ArrowLeft className="w-3 h-3" /> Wstecz
        </button>
        <Button
          onClick={handleAnalyze}
          disabled={!url.trim()}
          className="bg-[#ef3e29] hover:bg-[#ef3e29]/90 text-white disabled:opacity-40"
        >
          <Sparkles className="w-4 h-4 mr-1.5" /> Przygotuj reklamę
        </Button>
      </div>
    </div>
  );
}
